import Decimal from 'decimal.js';
import type { TransportDocumentType } from '../document/document-types';
import type { TradeInput } from './schema';
import { parsePackingValue } from './settlement-calculations';
import type { ValidationIssue } from './validation';

export function validateTransport(trade: TradeInput, type: TransportDocumentType): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  const add = (path: string, message: string, severity: 'ERROR' | 'WARNING' = 'ERROR') => {
    issues.push({ code: 'TRANSPORT_FIELD_INVALID', path, severity, message });
  };
  const require = (field: keyof TradeInput, label: string, severity: 'ERROR' | 'WARNING' = 'ERROR') => {
    if (!trade[field]) add(field, `${label}需要补充。`, severity);
  };
  require('transportDate', '单证日期');
  if (type === 'AIR_WAYBILL') {
    for (const [field, label] of [['awbNumber', '空运单号'], ['departureAirport', '始发机场'], ['destinationAirport', '目的机场'], ['flightNumber', '航班号'], ['flightDate', '航班日期'], ['currency', '币种']] as const) require(field, label);
    for (const field of ['weightPrepaid', 'weightCollect', 'otherPrepaid', 'otherCollect', 'taxPrepaid', 'taxCollect'] as const) require(field, '空运费用（无费用填 0）', 'WARNING');
  } else {
    for (const [field, label] of [['vessel', '船名'], ['voyageNumber', '航次'], ['portOfLoading', '起运港'], ['portOfDischarge', '卸货港']] as const) require(field, label);
  }
  if (type === 'BILL_OF_LADING' || type === 'AIR_WAYBILL' || type === 'SHIPPING_INSTRUCTION') {
    require('shipper', '托运人'); require('consignee', '收货人');
    require('notifyParty', '通知方', 'WARNING');
  }
  if (type === 'BILL_OF_LADING' || type === 'SHIPPING_INSTRUCTION') {
    require('blServiceType', '出单类型'); require('freightTerms', '运费条款'); require('originals', '正本份数');
    const service = trade.blServiceType?.toUpperCase();
    if (service && !['ORIGINAL', 'TELEX RELEASE', 'TELEFAX RELEASE', 'SEAWAY', '正本', '电放', '海运单'].includes(service)) add('blServiceType', '请选择正本 ORIGINAL、电放 TELEX RELEASE 或 SEAWAY。');
    if (['ORIGINAL', '正本'].includes(service ?? '') && trade.originals === '0') add('originals', '正本提单的正本份数必须大于零。');
    if (['SEAWAY', '海运单'].includes(service ?? '') && trade.originals != null && trade.originals !== '0') add('originals', 'SEAWAY 的正本份数应为 0。');
    if (type === 'BILL_OF_LADING') { require('billOfLadingNumber', '提单号'); require('placeOfIssue', '签发地'); }
    else { require('bookingNumber', '订舱号'); require('forwarder', '货代/船公司 TO'); require('blConsignee', '提单抬头', 'WARNING'); }
  }
  if (type === 'MANIFEST') {
    require('carrierName', '船公司'); require('voyageType', '航次类型', 'WARNING'); require('preparedBy', '编制人', 'WARNING');
    require('manifestDeclaration', '申报声明原文', 'WARNING');
  }
  if (type === 'SHIPPING_ADVICE') {
    for (const [field, label] of [['consignee', '收件人 TO'], ['invoiceNumber', '发票号'], ['billOfLadingNumber', '提单号'], ['currency', '币种'], ['etd', '实际离港时间']] as const) require(field, label);
    require('eta', '预计到港时间', 'WARNING'); require('dispatchedDocuments', '已寄单据', 'WARNING');
  }
  require(type === 'SHIPPING_ADVICE' || type === 'SHIPPING_INSTRUCTION' ? 'authorizedSignature' : 'carrierSignature', '签发人/承运人签章', 'WARNING');
  if (!trade.items.length) add('items', '至少需要一行货物明细。');
  trade.items.forEach((item, index) => {
    const path = (field: string) => `items.${index}.${field}`;
    if (!item.name && !item.description) add(path('name'), '品名或品名规格需要补充。');
    if (item.quantity == null || new Decimal(item.quantity).lte(0)) add(path('quantity'), '数量必须大于零。');
    else if (type !== 'SHIPPING_ADVICE' && !new Decimal(item.quantity).isInteger()) add(path('quantity'), '货物件数必须为整数。');
    for (const [field, kind, label] of [['grossWeight', 'weight', '毛重 (kg)'], ['measurement', 'volume', '体积 (m³)']] as const) {
      const parsed = parsePackingValue(item[field], kind);
      if (!parsed || parsed.amount.lte(0)) add(path(field), `${label}必须为大于零的有效数值。`);
    }
    const gross = parsePackingValue(item.grossWeight, 'weight');
    if (type !== 'AIR_WAYBILL' && type !== 'MANIFEST' && item.netWeight) {
      const net = parsePackingValue(item.netWeight, 'weight');
      if (!net || gross && net.amount.gt(gross.amount)) add(path('netWeight'), '净重须使用 kg，且不能大于毛重。');
    }
    if (type === 'AIR_WAYBILL') {
      const chargeable = parsePackingValue(item.chargeableWeight, 'weight');
      if (!chargeable || chargeable.amount.lte(0) || gross && chargeable.amount.lt(gross.amount)) add(path('chargeableWeight'), '计费重量须使用 kg，且不能小于毛重。');
    }
    if (type === 'MANIFEST') {
      for (const [field, label] of [['billOfLadingNumber', '提单号'], ['shipper', '托运人'], ['consignee', '收货人']] as const) if (!item[field]) add(path(field), `本行${label}需要补充。`);
    }
    if (type === 'SHIPPING_ADVICE') {
      if (!item.unit) add(path('unit'), '数量单位需要补充。');
      if (item.unitPrice == null) add(path('unitPrice'), '单价需要补充。');
    }
  });
  return issues;
}
